import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import useApiUrls from "../useApiUrls";

const CommonSendComponent = ({ 
  config 
}) => {
  const { merchant, pos } = useParams();
  const [activeTab, setActiveTab] = useState("single");
  const [merchantname, setMerchantname] = useState(merchant || "All");
  const [storeid, setStoreid] = useState("All");
  const [posid, setPosid] = useState(pos || "0");
  const [itemId, setItemId] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [merchantList, setMerchantList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [posList, setPosList] = useState([]);
  const [itemList, setItemList] = useState([]);

  // Group states
  const [selectedSubGroups, setSelectedSubGroups] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [subGroupId, setSubGroupId] = useState("");
  const [subGroupLoading, setSubGroupLoading] = useState(false);
  const [subGroupResponseData, setSubGroupResponseData] = useState(null);
  const [subGroupErrorMessage, setSubGroupErrorMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { apiUrls } = useApiUrls();
  const levels = apiUrls?.find((u) => u.operationType === "CatalogLevels")?.levels || [];

  const [levelid, setLevelid] = useState("0");
  const [subGroupLevelId, setSubGroupLevelId] = useState("0");

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get cookie helper
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  };

  // Fetch merchants
  const fetchMerchants = async () => {
    try {
      const token = getCookie("token");
      const userid = getCookie("userid");
      const response = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/SubUserPOSOperation/SubUser/GetDistinct/merchantid",
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const merchants = response.data.data.map((item) => item.merchantid);
        setMerchantList(merchants);
      }
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  // Fetch Stores by merchant
  const fetchStoresByMerchant = async (merchantName) => {
    try {
      const token = getCookie("token");
      const userid = getCookie("userid");
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/SubUserPOSOperation/SubUser/GetDistinct/specificstoreidbymerchantid_active/${merchantName}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setStoreList(response.data.data.map((item) => item.value));
      } else {
        setStoreList([]);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  };

  // Fetch POS by store
  const fetchPosByStore = async (storeId) => {
    try {
      const token = getCookie("token");
      const userid = getCookie("userid");
      const response = await axios.get(
        `https://api.dqr-display-tms.eventum.co.in/api/SubUserPOSOperation/SubUser/GetDistinct/specificposbystoreid_active/${storeId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const posIds = response.data.data.map((item) => ({
          id: item.id,
          value: item.value,
        }));
        setPosList(posIds);
      } else {
        setPosList([]);
      }
    } catch (error) {
      console.error("Error fetching POS:", error);
    }
  };

  // Fetch items (campaigns, audio, etc.)
  const fetchItems = async () => {
    if (!config.itemApiUrl) return;
    
    try {
      const token = getCookie("token");
      let response;
      
      // Special handling for campaigns (POST request with search body)
      if (config.itemName === 'campaign') {
        const requestBody = {
          ispageload: false,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: false,
          pagedirection: "",
        };
        
        response = await axios.post(config.itemApiUrl, requestBody, {
          headers: {
            "X-Authorization": token,
            UserId: getCookie("userid"),
            "Content-Type": "application/json",
          },
        });
      } else {
        // Standard GET request for other items
        response = await axios.get(config.itemApiUrl, {
          headers: {
            "X-Authorization": token,
            UserId: getCookie("userid"),
            accept: "text/plain",
          },
        });
      }

      if (response.data && Array.isArray(response.data.data)) {
        setItemList(response.data.data);
      }
    } catch (error) {
      console.error(`Error fetching ${config.itemName}:`, error);
    }
  };

  // Fetch groups
  const fetchGroups = async () => {
    try {
      const token = getCookie("token");
      const userid = getCookie("userid");
      const response = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/GroupAndSubGroup",
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setGroupList(response.data.data);
        // Select all subgroups by default on page load
        const allSubgroupIds = [];
        response.data.data.forEach((group) => {
          if (group.subgroupdata && Array.isArray(group.subgroupdata)) {
            group.subgroupdata.forEach((subgroup) => {
              allSubgroupIds.push(subgroup.id);
            });
          }
        });
        setSelectedSubGroups(allSubgroupIds);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  // Handle group selection (select all subgroups in a group)
  const handleGroupSelect = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata) return;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    const allSelected = subgroupIds.every((id) =>
      selectedSubGroups.includes(id)
    );

    if (allSelected) {
      // Deselect all subgroups in this group
      setSelectedSubGroups(
        selectedSubGroups.filter((id) => !subgroupIds.includes(id))
      );
    } else {
      // Select all subgroups in this group
      const newSelection = [...selectedSubGroups];
      subgroupIds.forEach((id) => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      setSelectedSubGroups(newSelection);
    }
  };

  // Handle individual subgroup selection
  const handleSubGroupSelect = (subgroupId) => {
    if (selectedSubGroups.includes(subgroupId)) {
      setSelectedSubGroups(
        selectedSubGroups.filter((id) => id !== subgroupId)
      );
    } else {
      setSelectedSubGroups([...selectedSubGroups, subgroupId]);
    }
  };

  // Check if all subgroups in a group are selected
  const isGroupFullySelected = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata || group.subgroupdata.length === 0)
      return false;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    return subgroupIds.every((id) => selectedSubGroups.includes(id));
  };

  // Check if some (but not all) subgroups in a group are selected
  const isGroupPartiallySelected = (groupId) => {
    const group = groupList.find((g) => g.id === groupId);
    if (!group || !group.subgroupdata || group.subgroupdata.length === 0)
      return false;

    const subgroupIds = group.subgroupdata.map((sg) => sg.id);
    const selectedCount = subgroupIds.filter((id) =>
      selectedSubGroups.includes(id)
    ).length;

    return selectedCount > 0 && selectedCount < subgroupIds.length;
  };

  // Handle send single item
  const handleSendSingle = async () => {
    if (config.showLevels && (!levelid || levelid === "0")) {
      setErrorMessage("Please select a level");
      return;
    }
    setLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      merchantname,
      storeid,
      posid: posid || "0",
      [config.itemIdField]: parseInt(itemId),
      ...(config.showLevels && { levelid: parseInt(levelid) || 0 }),
    };

    try {
      const response = await axios.post(config.singleApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.sendresult) {
        setResponseData(response.data.sendresult);
        setErrorMessage("");
      } else {
        setResponseData([
          {
            merchantname: merchantname,
            posname: posid || "All",
            responsestatus: response.data.responsestatus || "FAIL",
            errormsg: response.data.errormsg || "No matching records found",
          },
        ]);
        setErrorMessage("");
      }

      setLoading(false);
    } catch (error) {
      console.error(`Error sending ${config.itemName}:`, error);
      setErrorMessage(error.response?.data?.errormsg || `Error sending ${config.itemName}`);
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (config.showLevels && (!levelid || levelid === "0")) {
      setErrorMessage("Please select a level");
      return;
    }
    setLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      merchantname,
      storeid,
      posid: parseInt(posid) || 0,
      [config.itemIdField]: parseInt(itemId),
      ...(config.showLevels && { levelid: parseInt(levelid) || 0 }),
    };

    try {
      const response = await axios.post(config.deleteApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      });

      if (response.data && response.data.sendresult) {
        setResponseData(response.data.sendresult);
        setErrorMessage("");
      } else {
        setResponseData([
          {
            merchantname: merchantname,
            posname: posid || "All",
            responsestatus: response.data.responsestatus || "FAIL",
            errormsg: response.data.errormsg || "Delete failed",
          },
        ]);
        setErrorMessage("");
      }

      setLoading(false);
    } catch (error) {
      console.error(`Error deleting ${config.itemName}:`, error);
      setErrorMessage(error.response?.data?.errormsg || `Error deleting ${config.itemName}`);
      setLoading(false);
    }
  };

  // Handle delete level
  const handleDeleteLevel = async () => {
    if (config.showLevels && (!levelid || levelid === "0")) {
      setErrorMessage("Please select a level");
      return;
    }
    setLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      merchantname,
      storeid,
      posid: parseInt(posid) || 0,
      [config.itemIdField]: parseInt(itemId),
      ...(config.showLevels && { levelid: parseInt(levelid) || 0 }),
    };

    try {
      const response = await axios.post(config.deleteLevelApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      });

      if (response.data && response.data.sendresult) {
        setResponseData(response.data.sendresult);
        setErrorMessage("");
      } else {
        setResponseData([
          {
            merchantname: merchantname,
            posname: posid || "All",
            responsestatus: response.data.responsestatus || "FAIL",
            errormsg: response.data.errormsg || "Delete Level failed",
          },
        ]);
        setErrorMessage("");
      }

      setLoading(false);
    } catch (error) {
      console.error(`Error deleting level for ${config.itemName}:`, error);
      setErrorMessage(error.response?.data?.errormsg || `Error deleting level for ${config.itemName}`);
      setLoading(false);
    }
  };

  // Handle delete all
  const handleDeleteAll = async () => {
    setLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      merchantname,
      storeid,
      posid: parseInt(posid) || 0,
    };

    try {
      const response = await axios.post(config.deleteAllApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      });

      if (response.data && response.data.sendresult) {
        setResponseData(response.data.sendresult);
        setErrorMessage("");
      } else {
        setResponseData([
          {
            merchantname: merchantname,
            posname: posid || "All",
            responsestatus: response.data.responsestatus || "FAIL",
            errormsg: response.data.errormsg || "Delete All failed",
          },
        ]);
        setErrorMessage("");
      }

      setLoading(false);
    } catch (error) {
      console.error(`Error deleting all ${config.itemName}:`, error);
      setLoading(false);
    }
  };

  // Handle send to subgroup
  const handleSendToSubGroup = async () => {
    if (selectedSubGroups.length === 0) {
      setSubGroupErrorMessage("Please select at least one subgroup");
      return;
    }

    if (!subGroupId) {
      setSubGroupErrorMessage(`Please select a ${config.itemName}`);
      return;
    }

    if (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0")) {
      setSubGroupErrorMessage("Please select a level");
      return;
    }

    setSubGroupLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      subgroupids: selectedSubGroups,
      [config.itemIdField]: parseInt(subGroupId),
      ...(config.showLevels && { levelid: parseInt(subGroupLevelId) || 0 }),
    };

    try {
      const response = await axios.post(config.groupApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data) {
        setSubGroupResponseData(response.data);
        setSubGroupErrorMessage("");
        setSelectedSubGroups([]);
        setSubGroupId("");
        setSubGroupLevelId("0");
      }

      setSubGroupLoading(false);
    } catch (error) {
      console.error(`Error sending ${config.itemName} to subgroup:`, error);
      setSubGroupErrorMessage(
        error.response?.data?.errormsg || `Error sending ${config.itemName} to subgroup` 
      );
      setSubGroupLoading(false);
    }
  };

  // Handle group delete
  const handleGroupDelete = async () => {
    if (selectedSubGroups.length === 0) {
      setSubGroupErrorMessage("Please select at least one subgroup");
      return;
    }

    if (!subGroupId) {
      setSubGroupErrorMessage(`Please select a ${config.itemName}`);
      return;
    }

    if (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0")) {
      setSubGroupErrorMessage("Please select a level");
      return;
    }

    setSubGroupLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      subgroupids: selectedSubGroups,
      [config.itemIdField]: parseInt(subGroupId),
      ...(config.showLevels && { levelid: parseInt(subGroupLevelId) || 0 }),
    };

    try {
      const response = await axios.post(config.groupDeleteApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data) {
        setSubGroupResponseData(response.data);
        setSubGroupErrorMessage("");
        setSelectedSubGroups([]);
        setSubGroupId("");
        setSubGroupLevelId("0");
      }

      setSubGroupLoading(false);
    } catch (error) {
      console.error(`Error deleting ${config.itemName} from subgroup:`, error);
      setSubGroupErrorMessage(
        error.response?.data?.errormsg || `Error deleting ${config.itemName} from subgroup`
      );
      setSubGroupLoading(false);
    }
  };

  // Handle group delete level
  const handleGroupDeleteLevel = async () => {
    if (selectedSubGroups.length === 0) {
      setSubGroupErrorMessage("Please select at least one subgroup");
      return;
    }

    if (!subGroupId) {
      setSubGroupErrorMessage(`Please select a ${config.itemName}`);
      return;
    }

    if (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0")) {
      setSubGroupErrorMessage("Please select a level");
      return;
    }

    setSubGroupLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      subgroupids: selectedSubGroups,
      [config.itemIdField]: parseInt(subGroupId),
      ...(config.showLevels && { levelid: parseInt(subGroupLevelId) || 0 }),
    };

    try {
      const response = await axios.post(config.groupDeleteLevelApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data) {
        setSubGroupResponseData(response.data);
        setSubGroupErrorMessage("");
        setSelectedSubGroups([]);
        setSubGroupId("");
        setSubGroupLevelId("0");
      }

      setSubGroupLoading(false);
    } catch (error) {
      console.error(`Error deleting level for ${config.itemName} from subgroup:`, error);
      setSubGroupErrorMessage(
        error.response?.data?.errormsg || `Error deleting level for ${config.itemName} from subgroup`
      );
      setSubGroupLoading(false);
    }
  };

  // Handle group delete all
  const handleGroupDeleteAll = async () => {
    if (selectedSubGroups.length === 0) {
      setSubGroupErrorMessage("Please select at least one subgroup");
      return;
    }

    setSubGroupLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const requestBody = {
      subgroupids: selectedSubGroups,
    };

    try {
      const response = await axios.post(config.groupDeleteAllApiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data) {
        setSubGroupResponseData(response.data);
        setSubGroupErrorMessage("");
        setSelectedSubGroups([]);
        setSubGroupId("");
      }

      setSubGroupLoading(false);
    } catch (error) {
      console.error(`Error deleting all ${config.itemName} from subgroup:`, error);
      setSubGroupErrorMessage(
        error.response?.data?.errormsg || `Error deleting all ${config.itemName} from subgroup`
      );
      setSubGroupLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchGroups();
  }, []);

  return (
    <div className="content-wrapper" style={{ padding: "20px" }}>
      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "20px",
          backgroundColor: "var(--surface)",
          borderRadius: "8px",
          width: "600px",
          margin: "0 auto 20px",
          borderBottom: "1px solid var(--border)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <button
          style={{
            flex: 1,
            padding: "12px",
            backgroundColor:
              activeTab === "single" ? "#007bff" : "transparent",
            color: activeTab === "single" ? "#fff" : "#333",
            border: "none",
            cursor: "pointer",
            borderRadius: "8px 0 0 0",
            fontWeight: activeTab === "single" ? "bold" : "normal",
            transition: "all 0.3s ease",
            fontSize: "14px",
            boxShadow: activeTab === "single" ? "0 2px 4px rgba(0,123,255,0.3)" : "none",
          }}
          onClick={() => {
            setActiveTab("single");
            setErrorMessage("");
            setSubGroupErrorMessage("");
          }}
        >
          Single
        </button>
        <button
          style={{
            flex: 1,
            padding: "12px",
            backgroundColor:
              activeTab === "group" ? "#007bff" : "transparent",
            color: activeTab === "group" ? "#fff" : "#333",
            border: "none",
            cursor: "pointer",
            borderRadius: "0 8px 0 0",
            fontWeight: activeTab === "group" ? "bold" : "normal",
            transition: "all 0.3s ease",
            fontSize: "14px",
            boxShadow: activeTab === "group" ? "0 2px 4px rgba(0,123,255,0.3)" : "none",
          }}
          onClick={() => {
            setActiveTab("group");
            setErrorMessage("");
            setSubGroupErrorMessage("");
          }}
        >
          Group
        </button>
      </div>

      {/* Single Tab Content */}
      {activeTab === "single" && (
        <div
          style={{
            backgroundColor: "var(--surface)",
            padding: "2rem",
            borderRadius: "8px",
            width: "600px",
            margin: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h4 style={{ marginBottom: "20px" }}>{config.singleTitle}</h4>
          <div className="form-group">
            <label>Merchant Name</label>
            {merchant ? (
              <select className="form-control" value={merchantname} disabled>
                <option value={merchant}>{merchant}</option>
              </select>
            ) : (
              <select
                className="form-control"
                value={merchantname}
                onChange={(e) => {
                  const selectedMerchant = e.target.value;
                  setMerchantname(selectedMerchant);
                  setStoreid("");
                  setPosid("");
                  if (selectedMerchant === "All") {
                    setStoreid("All");
                    setPosid("0");
                  } else if (selectedMerchant && selectedMerchant !== "Select") {
                    fetchStoresByMerchant(selectedMerchant);
                  } else {
                    setStoreList([]);
                    setPosList([]);
                  }
                }}
                onFocus={fetchMerchants}
              >
                <option value="Select">Select</option>
                <option value="All">All</option>
                {merchantList.map((merchant, index) => (
  <option key={index} value={merchant}>
    {merchant}  {/* Changed from merchantid to merchant */}
  </option>
))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Store ID</label>
            <select
              className="form-control"
              value={storeid}
              onChange={(e) => {
                const selectedStore = e.target.value;
                setStoreid(selectedStore);
                setPosid("");
                if (selectedStore && selectedStore !== "All") {
                  fetchPosByStore(selectedStore);
                } else {
                  setPosList([]);
                }
              }}
              disabled={!merchantname || merchantname === "Select"}
              style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
            >
              <option value="">Select Store</option>
              <option value="All">All</option>
              {storeList.map((store, index) => (
                <option key={index} value={store}>
                  {store}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>POS ID</label>
            {pos ? (
              <select className="form-control" value={posid} disabled>
                <option value={pos}>{pos}</option>
              </select>
            ) : (
              <select
                className="form-control"
                value={posid}
                onChange={(e) => setPosid(e.target.value)}
                disabled={!storeid}
                style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
              >
                <option value="0">All</option>
                {posList.map((pos, index) => (
                  <option key={index} value={pos.id}>
                    {pos.value}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>{config.itemLabel}</label>
            <select
              className="form-control"
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
            >
              <option value="">Select {config.itemName}</option>
              {itemList.map((item, index) => (
                <option key={index} value={item.id}>
                  {item[config.itemDisplayField] || item.name || item.value}
                </option>
              ))}
            </select>
          </div>
          {config.showLevels && (
            <div className="form-group">
              <label>Level</label>
              <select
                className="form-control"
                value={levelid}
                onChange={(e) => {
                  setLevelid(e.target.value);
                  if (e.target.value !== "0") {
                    setErrorMessage("");
                  }
                }}
              >
                <option value="0">Select Level</option>
                {levels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    Level {lvl}
                  </option>
                ))}
              </select>
            </div>
          )}
          {errorMessage && (
            <div style={{ color: "#dc3545", marginBottom: "15px", fontSize: "14px", fontWeight: "500" }}>
              ⚠️ {errorMessage}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleSendSingle}
              disabled={loading || !itemId || (config.showLevels && (!levelid || levelid === "0"))}
            >
              {loading ? "Sending..." : config.singleButtonText}
            </button>
            {config.deleteApiUrl && (
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={loading || !itemId || (config.showLevels && (!levelid || levelid === "0"))}
                style={{ marginLeft: "10px" }}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            )}
            {config.deleteLevelApiUrl && (
              <button
                className="btn btn-danger"
                onClick={handleDeleteLevel}
                disabled={loading || !itemId || (config.showLevels && (!levelid || levelid === "0"))}
                style={{ marginLeft: "10px" }}
              >
                {loading ? "Deleting..." : "Delete Level"}
              </button>
            )}
            {config.deleteAllApiUrl && (
              <button
                className="btn btn-warning"
                onClick={handleDeleteAll}
                disabled={loading}
                style={{ marginLeft: "10px", color: "#fff" }}
              >
                {loading ? "Deleting All..." : "Delete All"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Group Tab Content */}
      {activeTab === "group" && (
        <div
          style={{
            backgroundColor: "var(--surface)",
            padding: "2rem",
            borderRadius: "8px",
            width: "600px",
            margin: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h4 style={{ marginBottom: "20px" }}>{config.groupTitle}</h4>

          <div className="form-group">
            <label>Select Subgroups</label>
            <div ref={dropdownRef} style={{ position: "relative" }}>
              {/* Dropdown Toggle */}
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: "var(--surface)",
                  minHeight: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ color: selectedSubGroups.length === 0 ? "var(--text-muted)" : "var(--text)" }}>
                  {selectedSubGroups.length === 0
                    ? "Select subgroups..."
                    : `${selectedSubGroups.length} subgroup(s) selected`}
                </span>
                <i
                  className={`fas fa-chevron-${
                    isDropdownOpen ? "up" : "down"
                  }`}
                  style={{ color: "var(--text)" }}
                ></i>
              </div>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "4px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  {groupList.length > 0 ? (
                    groupList.map((group) => (
                      <div key={group.id}>
                        {group.subgroupdata &&
                          group.subgroupdata.length > 0 && (
                            <div>
                              {/* Group Header */}
                              <div
                                onClick={() => handleGroupSelect(group.id)}
                                style={{
                                  padding: "10px 15px",
                                  backgroundColor: "var(--surface2)",
                                  borderBottom: "1px solid var(--border)",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface2)")
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={isGroupFullySelected(group.id)}
                                  ref={(input) => {
                                    if (input) {
                                      input.indeterminate =
                                        isGroupPartiallySelected(group.id);
                                    }
                                  }}
                                  style={{ marginRight: "8px" }}
                                  onChange={() => handleGroupSelect(group.id)}
                                />
                                {group.name}
                              </div>
                              {/* Subgroups */}
                              {group.subgroupdata.map((subgroup) => (
                                <div
                                  key={subgroup.id}
                                  style={{
                                    padding: "8px 15px 8px 35px",
                                    backgroundColor: "var(--surface)",
                                    borderBottom: "1px solid var(--border)",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    display: "flex",
                                    alignItems: "center",
                                    transition: "background-color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "var(--surface2)")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "var(--surface)")
                                  }
                                  onClick={() => handleSubGroupSelect(subgroup.id)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSubGroups.includes(
                                      subgroup.id
                                    )}
                                    style={{ marginRight: "8px" }}
                                    onChange={() =>
                                      handleSubGroupSelect(subgroup.id)
                                    }
                                  />
                                  {subgroup.name}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "15px",
                        textAlign: "center",
                        color: "var(--text-muted)",
                      }}
                    >
                      No groups available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{config.itemLabel}</label>
            <select
              className="form-control"
              value={subGroupId}
              onChange={(e) => setSubGroupId(e.target.value)}
            >
              <option value="">Select {config.itemName}</option>
              {itemList.map((item, index) => (
                <option key={index} value={item.id}>
                  {item[config.itemDisplayField] || item.name || item.value}
                </option>
              ))}
            </select>
          </div>
          {config.showLevels && (
            <div className="form-group">
              <label>Level</label>
              <select
                className="form-control"
                value={subGroupLevelId}
                onChange={(e) => {
                  setSubGroupLevelId(e.target.value);
                  if (e.target.value !== "0") {
                    setSubGroupErrorMessage("");
                  }
                }}
              >
                <option value="0">Select Level</option>
                {levels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    Level {lvl}
                  </option>
                ))}
              </select>
            </div>
          )}
          {subGroupErrorMessage && (
            <div style={{ color: "#dc3545", marginBottom: "15px", fontSize: "14px", fontWeight: "500" }}>
              ⚠️ {subGroupErrorMessage}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleSendToSubGroup}
              disabled={subGroupLoading || !subGroupId || (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0"))}
            >
              {subGroupLoading ? "Sending..." : config.groupButtonText}
            </button>
            {config.groupDeleteApiUrl && (
              <button
                className="btn btn-danger"
                onClick={handleGroupDelete}
                disabled={subGroupLoading || !subGroupId || selectedSubGroups.length === 0 || (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0"))}
                style={{ marginLeft: "10px" }}
              >
                {subGroupLoading ? "Deleting..." : "Delete Group"}
              </button>
            )}
            {config.groupDeleteLevelApiUrl && (
              <button
                className="btn btn-danger"
                onClick={handleGroupDeleteLevel}
                disabled={subGroupLoading || !subGroupId || selectedSubGroups.length === 0 || (config.showLevels && (!subGroupLevelId || subGroupLevelId === "0"))}
                style={{ marginLeft: "10px" }}
              >
                {subGroupLoading ? "Deleting..." : "Delete Level Group"}
              </button>
            )}
            {config.groupDeleteAllApiUrl && (
              <button
                className="btn btn-warning"
                onClick={handleGroupDeleteAll}
                disabled={subGroupLoading || selectedSubGroups.length === 0}
                style={{ marginLeft: "10px", color: "#fff" }}
              >
                {subGroupLoading ? "Deleting All..." : "Delete All Group"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Response Display */}
      {responseData && (
        <div
          style={{
            backgroundColor: "var(--surface)",
            padding: "1rem",
            borderRadius: "8px",
            width: "100%",
            margin: "20px auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h5>Campaign Response Results</h5>
          {Array.isArray(responseData) && responseData.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "1rem",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                  }}
                >
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Merchant Name
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Store ID
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    POS Name
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Response Status
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Message/Error
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#495057",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    Status Badge
                  </th>
                </tr>
              </thead>
              <tbody>
                {responseData.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: "1px solid #dee2e6",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      Single
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      {item.merchantname || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      {item.storeid || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      {item.posname || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                        fontWeight: "500",
                      }}
                    >
                      {item.responsestatus || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                        color: "#6c757d",
                      }}
                    >
                      {item.errormsg || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor:
                            item.responsestatus === "SUCCESS"
                              ? "#d4edda"
                              : "#f8d7da",
                          color:
                            item.responsestatus === "SUCCESS"
                              ? "#155724"
                              : "#721c24",
                        }}
                      >
                        {item.responsestatus === "SUCCESS" ? "✓" : "✗"}{" "}
                        {item.responsestatus === "SUCCESS" ? "Success" : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: "1rem",
                textAlign: "center",
                color: "#6c757d",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                marginTop: "1rem",
              }}
            >
              No response data available
            </div>
          )}
        </div>
      )}

      {/* Subgroup Response Display */}

      {/* Subgroup Response Display */}
      {subGroupResponseData && (
        <div
          style={{
            backgroundColor: "var(--surface)",
            padding: "1rem",
            borderRadius: "8px",
            width: "100%",
            margin: "20px auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h5 style={{ fontWeight: "600", color: "var(--text)", marginBottom: "15px" }}>Group Response Results</h5>
          {subGroupResponseData.sendresult && Array.isArray(subGroupResponseData.sendresult) && subGroupResponseData.sendresult.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "1rem",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                  }}
                >
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>Type</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>Merchant Name</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>POS Name</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>Response Status</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>Message/Error</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#495057", borderBottom: "1px solid #dee2e6" }}>Status Badge</th>
                </tr>
              </thead>
              <tbody>
                {subGroupResponseData.sendresult.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: "1px solid #dee2e6",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                    }}
                  >
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6" }}>Group</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6" }}>{item.merchantname || "N/A"}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6" }}>{item.posname || "N/A"}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6", fontWeight: "500" }}>{item.responsestatus || "N/A"}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6", color: "#6c757d" }}>{item.errormsg || "N/A"}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #dee2e6" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor: item.responsestatus === "SUCCESS" ? "#d4edda" : "#f8d7da",
                          color: item.responsestatus === "SUCCESS" ? "#155724" : "#721c24",
                        }}
                      >
                        {item.responsestatus === "SUCCESS" ? "✓" : "✗"} {item.responsestatus === "SUCCESS" ? "Success" : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: "1rem",
                textAlign: "center",
                color: "#6c757d",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                marginTop: "1rem",
              }}
            >
              {subGroupResponseData.errormsg || "No response data available"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommonSendComponent;
